<template>
  <v-container class="page--financial-adjustments" fluid>
    <ActionBar />

    <div :class="[
      'page__content',
      {
        'page__content--from-v4': noMainMenuFromV4
      }]">
      <v-tabs
        v-model="activeTab"
        slider-color="secondary"
        class="v-tabs-reports"
      >
        <v-tab ripple>
          {{ $t(`${namespace}.tabs.tabsList.rides`) }}
        </v-tab>

        <v-tab v-if="hasRole('billing', 'ROLE_GET_CHARGEBACK')" ripple>
          {{ $t(`${namespace}.tabs.tabsList.chargeback`) }}
        </v-tab>

        <v-tab v-if="hasRole('billing', 'ROLE_GET_FIND_CREDITS_WITH_BALANCE')" ripple>
          {{ $t(`${namespace}.tabs.tabsList.credit`) }}
        </v-tab>

        <v-tabs-items touchless>
          <v-tab-item>
            <TabRide
              :activeTab="activeTab"
              :data="ride"
              @loadData="loadRideData"
              @updateFilters="updateRideFilters"
              @updatePagination="updateRidePagination"
              @resetPagination="resetRidePagination"
              @changeTab="setActiveTabAndReloadData"
            />
          </v-tab-item>

          <v-tab-item v-if="hasRole('billing', 'ROLE_GET_CHARGEBACK')">
            <TabChargeback
              :activeTab="activeTab"
              :data="chargeBack"
              @loadData="loadChargeBackData"
              @updatePagination="updateChargeBackPagination"
              @resetPagination="resetChargeBackPagination"
              @changeTab="setActiveTabAndReloadData"
            />
          </v-tab-item>

          <v-tab-item v-if="hasRole('billing', 'ROLE_GET_FIND_CREDITS_WITH_BALANCE')">
            <TabCredits
              :activeTab="activeTab"
              :data="credit"
              @loadData="loadCreditData"
              @updatePagination="updateCreditPagination"
              @resetPagination="resetCreditPagination"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </div>
  </v-container>
</template>

<script>
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { mapState } from 'vuex'
import { isArray, hasRole } from 'utils/functions'
import ActionBar from 'components/financialAdjustments/ActionBar'
import TabRide from 'components/financialAdjustments/TabRide'
import TabChargeback from 'components/financialAdjustments/TabChargeback'
import TabCredits from 'components/financialAdjustments/TabCredits'

export default {
  name: 'FinancialAdjustmentsPage',
  components: {
    ActionBar,
    TabRide,
    TabChargeback,
    TabCredits
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      financialAccountId: ({ company }) => company.selectedCompany.financialAccountId,
      noMainMenuFromV4: ({ mainMenu }) => mainMenu.noMainMenuFromV4
    })
  },
  data () {
    return {
      namespace: 'Views.FinancialAdjustmentsPage',
      activeTab: null,

      // ride data
      ride: {
        status: null,
        filters: {},
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      },

      // chargeBack data
      chargeBack: {
        status: null,
        summarizeNumberCount: 0,
        summarizeValue: 0,
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      },

      // credit data
      credit: {
        status: null,
        summarizeNumberCount: 0,
        summarizeValue: 0,
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      }
    }
  },
  methods: {
    isValidArray (val) {
      return isArray(val)
    },

    hasRole (group, role) {
      return hasRole(group, role)
    },

    redirectToHome () {
      this.$router.push({ name: 'Home' })
    },

    // ==========================================
    // RIDE METHODS
    // ==========================================

    updateRideFilters (filters) {
      this.ride.filters = { ...filters }
    },

    updateRidePaginationOnRequest (response, itemsPerPage) {
      this.ride.pagination = {
        ...this.ride.pagination,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      }
    },

    updateRidePagination (pagination) {
      this.ride.pagination = { ...pagination }
    },

    resetRidePagination () {
      this.ride.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    async loadRideData () {
      if (this.ride.status === STATUS_IS_LOADING) return

      this.ride.status = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.ride.pagination
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const { search: apiTaxiJobId } = this.ride.filters

        const { data: res } = await this.$apiService.getOpenInvoiceReceipts(
          { companyId },
          { apiTaxiJobId },
          { size: itemsPerPage },
          { page: currentPage - 1 },
          { orderBy: 'desc' }
        )

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          throw new Error(error)
        }

        this.ride.tableBody = []

        if (res.content) {
          this.ride.tableBody = res.content
          this.updateRidePaginationOnRequest(res, itemsPerPage)
        }

        this.ride.status = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.ride.status = STATUS_FAILED
      }
    },

    // ==========================================
    // CHARGEBACK METHODS
    // ==========================================

    updateChargeBackPaginationOnRequest (response, itemsPerPage) {
      this.chargeBack.pagination = {
        ...this.chargeBack.pagination,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      }
    },

    updateChargeBackPagination (pagination) {
      this.chargeBack.pagination = { ...pagination }
    },

    resetChargeBackPagination () {
      this.chargeBack.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    async loadChargeBackData () {
      if (!this.hasRole('billing', 'ROLE_GET_CHARGEBACK') || this.chargeBack.status === STATUS_IS_LOADING) {
        return
      }

      this.chargeBack.status = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.chargeBack.pagination
        const { data: res } = await this.$apiService.getChargeBackList(
          { financialAccountId: this.financialAccountId },
          { size: itemsPerPage },
          { page: currentPage - 1 },
          { orderBy: 'desc' }
        )

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          throw new Error(error)
        }

        this.chargeBack.tableBody = []

        if (res.content) {
          this.chargeBack.tableBody = res.content.map(item => {
            return {
              ...item,
              receipt: {
                corporateJob: {
                  ...item.ride.detail,
                  apiInfo: {
                    ...item.ride.detail.apiInfo,
                    apiTaxiJobId: item.ride.detail.apiInfo.apiTaxiJobId
                  }
                }
              },
              distance: item.ride.detail.odometer,
              amount: item.ride.amount
            }
          })

          this.chargeBack.summarizeNumberCount = res.totalChargeBack
          this.chargeBack.summarizeValue = res.totalValue
          this.updateChargeBackPaginationOnRequest(res, itemsPerPage)
        }

        this.chargeBack.status = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.chargeBack.status = STATUS_FAILED
      }
    },

    // ==========================================
    // CREDIT METHODS
    // ==========================================

    updateCreditPaginationOnRequest (response, itemsPerPage) {
      this.credit.pagination = {
        ...this.credit.pagination,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      }
    },

    updateCreditPagination (pagination) {
      this.credit.pagination = { ...pagination }
    },

    resetCreditPagination () {
      this.credit.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    async loadCreditData () {
      if (!this.hasRole('billing', 'ROLE_GET_FIND_CREDITS_WITH_BALANCE') || this.credit.status === STATUS_IS_LOADING) {
        console.log('return credt')
        return
      }

      this.credit.status = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.credit.pagination
        const { data: res } = await this.$apiService.getCreditList(
          { financialAccountId: this.financialAccountId },
          { size: itemsPerPage },
          { page: currentPage - 1 },
          { orderBy: 'desc' }
        )

        if (!res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          throw new Error(error)
        }

        this.credit.tableBody = []

        if (res.content) {
          this.credit.tableBody = res.content
          this.credit.summarizeNumberCount = res.numberOfElements
          this.credit.summarizeValue = res.filterSubTotal
          this.updateCreditPaginationOnRequest(res, itemsPerPage)
        }

        this.credit.status = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.credit.status = STATUS_FAILED
      }
    },

    setActiveTab (tab = 0) {
      this.activeTab = Number(tab)
    },

    async loadTabsData (tab = 0) {
      await Promise.all([
        this.loadRideData(),
        this.loadChargeBackData(),
        this.loadCreditData()
      ])

      this.setActiveTab(tab)
    },

    async setActiveTabAndReloadData (tab) {
      if (Number(tab) <= 1) {
        await Promise.all([
          this.loadRideData(),
          this.loadChargeBackData()
        ])
      }

      if (tab !== null && tab !== undefined) {
        setTimeout(() => { this.setActiveTab(tab) }, 600)
      }
    },

    activeCreditTab () {
      if (this.$route.query && this.$route.query.tab) {
        this.setActiveTab(this.$route.query.tab)
        this.$router.replace({})
      }
    },

    resetData () {
      // ride data
      this.ride = {
        status: null,
        filters: {},
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      }

      // chargeBack data
      this.chargeBack = {
        status: null,
        summarizeNumberCount: 0,
        summarizeValue: 0,
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      }

      // credit data
      this.credit = {
        status: null,
        summarizeNumberCount: 0,
        summarizeValue: 0,
        tableBody: [],
        pagination: {
          totalElements: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: 25
        }
      }
    }
  },
  watch: {
    selectedCompany (val) {
      if (!val) this.redirectToHome()
      this.resetData()
      this.loadTabsData(this.activeTab)
    }
  },
  async mounted () {
    await this.loadTabsData()
    this.activeCreditTab()
  }
}
</script>

<style lang="scss">
.page--financial-adjustments {

  .page__content {
    background-color: transparent;
    height: 100%;
  }

  .v-tabs-reports,
  .v-window,
  .v-window__container,
  .v-window-item,
  .v-window-item > div {
    height: 100%;
  }
}
</style>
